/**
 * Where files come in: a drop target that is also a button.
 *
 * Both routes exist because both are used. Someone with a folder open drags;
 * someone on a laptop trackpad clicks. The drop target keeps its own enter/leave
 * counter because dragging over a child element fires `dragleave` on the parent,
 * and a naive boolean makes the highlight flicker as the pointer crosses the
 * text inside it.
 */

import { useId, useRef, useState } from 'react';
import type { DragEvent, ReactElement } from 'react';
import { MAX_IMAGE_BYTES, MAX_VIDEO_BYTES, MAX_VIDEO_HEIGHT, MAX_VIDEO_WIDTH } from '@pbtv/shared';
import { formatBytes } from '../../lib/format.js';
import { SHARED_SCOPE_PARAM } from '../../lib/media.js';
import { parseBrands } from './presentation.js';

export interface ScopeOption {
  storeId: string | null;
  label: string;
}

export interface MediaDropzoneProps {
  scopes: readonly ScopeOption[];
  storeId: string | null;
  onStoreChange: (storeId: string | null) => void;
  brands: string;
  onBrandsChange: (brands: string) => void;
  /** Set when the caller cannot upload at all — no reachable store, say. */
  blockedReason: string | null;
  onFiles: (files: File[]) => void;
}

function scopeValue(storeId: string | null): string {
  return storeId ?? SHARED_SCOPE_PARAM;
}

export function MediaDropzone({
  scopes,
  storeId,
  onStoreChange,
  brands,
  onBrandsChange,
  blockedReason,
  onFiles,
}: MediaDropzoneProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const scopeId = useId();
  const brandsId = useId();

  const blocked = blockedReason !== null;

  const accept = (list: FileList | null): void => {
    if (list === null || list.length === 0) return;
    onFiles(Array.from(list));
  };

  const onDragEnter = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (blocked) return;
    depth.current += 1;
    setDragging(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (blocked) return;
    accept(event.dataTransfer.files);
  };

  const parsedBrands = parseBrands(brands);

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">Add files</h3>
          <p className="panel__subtitle">
            Files go straight from this browser to storage in parts, so the API never carries a
            video. Each one is checked here before a byte is sent.
          </p>
        </div>
      </div>

      <div className="control-row">
        <label className="field field--inline" htmlFor={scopeId}>
          <span className="field__label">Upload into</span>
          <select
            id={scopeId}
            className="input"
            value={scopeValue(storeId)}
            disabled={blocked}
            onChange={(event) =>
              onStoreChange(
                event.target.value === SHARED_SCOPE_PARAM ? null : event.target.value,
              )
            }
          >
            {scopes.map((scope) => (
              <option key={scopeValue(scope.storeId)} value={scopeValue(scope.storeId)}>
                {scope.label}
              </option>
            ))}
          </select>
        </label>

        {storeId === null ? (
          <label className="field field--inline media-brands" htmlFor={brandsId}>
            <span className="field__label">Brands</span>
            <input
              id={brandsId}
              className="input"
              type="text"
              value={brands}
              placeholder="Pizza Bros, Rosetown Kebab"
              disabled={blocked}
              onChange={(event) => onBrandsChange(event.target.value)}
            />
            <span className="field__hint">
              {parsedBrands.length === 0
                ? 'Leave empty and every brand can use these files.'
                : `Only ${parsedBrands.join(' and ')} will see them.`}
            </span>
          </label>
        ) : null}
      </div>

      <div
        className={dragging ? 'media-drop media-drop--active' : 'media-drop'}
        onDragEnter={onDragEnter}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <p className="media-drop__headline">
          {dragging ? 'Drop them here' : 'Drag videos and images here'}
        </p>
        <button
          type="button"
          className="button button--primary"
          disabled={blocked}
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </button>
        <input
          ref={inputRef}
          className="visually-hidden"
          type="file"
          multiple
          // Wide on purpose. A picker that hides .mov and .heic leaves someone
          // wondering why their file is not there; letting them choose it and
          // then telling them which setting made it is far more useful.
          accept="video/*,image/*,.mov,.heic,.heif"
          onChange={(event) => {
            accept(event.target.files);
            // Cleared so the same file picked twice in a row still fires.
            event.target.value = '';
          }}
        />
      </div>

      {blocked ? (
        <p className="alert alert--info" role="note">
          {blockedReason}
        </p>
      ) : null}

      <ul className="media-rules">
        <li>
          Video: H.264 MP4, up to {MAX_VIDEO_WIDTH} × {MAX_VIDEO_HEIGHT} and{' '}
          {formatBytes(MAX_VIDEO_BYTES)}. A clip for a screen turned on its side must be exported
          already rotated — a rotation flag is refused, because the TV would apply it a second time.
        </li>
        <li>
          Images: JPEG, PNG or WebP, up to {formatBytes(MAX_IMAGE_BYTES)}.
        </li>
        <li>
          An iPhone records HEVC and shoots HEIC by default and both are refused. Settings → Camera →
          Formats → Most Compatible fixes them together.
        </li>
      </ul>
    </section>
  );
}
